import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CSVLink } from "react-csv";
import dayjs from "dayjs";

const ActiveProjects = ({
  rows = [],
  loading = false,
  title = "Recent Receipts",
  viewType = "water", // ✅ "water" | "slrm"
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const recordsPage = 8;
  const lastIndex = currentPage * recordsPage;
  const firstIndex = lastIndex - recordsPage;

  // ✅ headers based on toggle
  const headers = useMemo(() => {
    const base = [
      { label: "Receipt No", key: "rct_no" },
      { label: "Family Head", key: "family_head" },
      { label: "Mode", key: "mode" },
    ];

    const fee =
      viewType === "slrm"
        ? [{ label: "SLRM Fee", key: "slrm_fee" }]
        : [{ label: "Water Fee", key: "water_fee" }];

    const tail = [
      { label: "Total Amount", key: "amount" },
      { label: "Receipt Date", key: "rct_date" },
    ];

    return [...base, ...fee, ...tail];
  }, [viewType]);

  const tableData = useMemo(() => {
    return (rows || []).map((r) => ({
      rct_no: r.rct_no,
      family_head: r.family_head || "-",
      mode: r.mode || "-",
      water_fee: Number(r.water_fee || 0),
      slrm_fee: Number(r.slrm_fee || 0),
      amount: Number(r.amount || 0),
      rct_date: r.rct_date ? dayjs(r.rct_date).format("DD-MMM-YYYY") : "-",
    }));
  }, [rows]);

  const records = tableData.slice(firstIndex, lastIndex);
  const npage = Math.max(1, Math.ceil(tableData.length / recordsPage));
  const number = [...Array(npage + 1).keys()].slice(1);

  function prePage(e) {
    e?.preventDefault?.();
    if (currentPage !== 1) setCurrentPage(currentPage - 1);
  }
  function changeCPage(e, id) {
    e?.preventDefault?.();
    setCurrentPage(id);
  }
  function nextPage(e) {
    e?.preventDefault?.();
    if (currentPage !== npage) setCurrentPage(currentPage + 1);
  }

  // ✅ fix page if rows shrink
  useEffect(() => {
    if (currentPage > npage) setCurrentPage(npage);
  }, [npage, currentPage]);

  const csvlink = useMemo(
    () => ({
      headers,
      data: tableData.map((r) => {
        // ✅ for csv also keep only selected fee column (optional clean)
        if (viewType === "slrm") {
          return {
            rct_no: r.rct_no,
            family_head: r.family_head,
            mode: r.mode,
            slrm_fee: r.slrm_fee,
            amount: r.amount,
            rct_date: r.rct_date,
          };
        }
        return {
          rct_no: r.rct_no,
          family_head: r.family_head,
          mode: r.mode,
          water_fee: r.water_fee,
          amount: r.amount,
          rct_date: r.rct_date,
        };
      }),
      filename: `receipts_${viewType}_${dayjs().format("YYYYMMDD_HHmm")}.csv`,
    }),
    [headers, tableData, viewType]
  );

  return (
    <div className="card">
      <div className="card-body p-0">
        <div className="table-responsive active-projects style-1">
          <div className="tbl-caption">
            <h4 className="heading mb-0">{title}</h4>
            <div>
              <CSVLink {...csvlink} className="btn btn-primary light btn-sm">
                <i className="fa-solid fa-file-excel" /> Export Report
              </CSVLink>
            </div>
          </div>

          <div className="dataTables_wrapper no-footer mb-0">
            <table className="table dataTable no-footer mb-2 mb-sm-0">
              <thead>
                <tr>
                  <th>Receipt No</th>
                  <th>Family Head</th>
                  <th>Mode</th>

                  {/* ✅ Only 1 fee column */}
                  {viewType === "slrm" ? (
                    <th className="text-end">SLRM Fee</th>
                  ) : (
                    <th className="text-end">Water Fee</th>
                  )}

                  <th className="text-end">Amount</th>
                  <th>Date</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No receipts found
                    </td>
                  </tr>
                ) : (
                  records.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <b>#{item.rct_no}</b>
                      </td>
                      <td>{item.family_head}</td>
                      <td>{item.mode}</td>

                      {/* ✅ Only selected fee value */}
                      {viewType === "slrm" ? (
                        <td className="text-end">₹{item.slrm_fee}</td>
                      ) : (
                        <td className="text-end">₹{item.water_fee}</td>
                      )}

                      <td className="text-end">
                        <b>₹{item.amount}</b>
                      </td>
                      <td>{item.rct_date}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* PAGINATION */}
            <div className="d-sm-flex text-center justify-content-between align-items-center px-3 pb-3">
              <div className="dataTables_info">
                Showing {tableData.length === 0 ? 0 : firstIndex + 1} to{" "}
                {Math.min(lastIndex, tableData.length)} of {tableData.length} entries
              </div>

              <div className="dataTables_paginate paging_simple_numbers justify-content-center">
                <Link className="paginate_button previous" to="#" onClick={prePage}>
                  <i className="fa-solid fa-angle-left" />
                </Link>

                <span>
                  {number.map((n) => (
                    <Link
                      to="#"
                      key={n}
                      className={`paginate_button ${currentPage === n ? "current" : ""}`}
                      onClick={(e) => changeCPage(e, n)}
                    >
                      {n}
                    </Link>
                  ))}
                </span>

                <Link className="paginate_button next" to="#" onClick={nextPage}>
                  <i className="fa-solid fa-angle-right" />
                </Link>
              </div>
            </div>
            {/* END PAGINATION */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveProjects;
